/**
 * Brother PES (버전 1, "#PES0001") + PEC 블록.
 *  - PES 부분: CEmbOne / CSewSeg 구간 목록 (PE-DESIGN 이 읽는 부분)
 *  - PEC 부분: 머리글(색 번호 목록) + 바늘땀(7비트/12비트) + 48×38 흑백 미리보기 그림 (자수기가 읽는 부분)
 * 구조와 상수는 pyembroidery (MIT) 의 PesWriter.py / PecWriter.py / PecGraphics.py 를 옮겼다. NOTICE.md 참고.
 * 차이: 트림이 없는 짧은 점프는 JUMP 표시(0x10), 트림 뒤 점프만 TRIM 표시(0x20)로 쓴다.
 */
import { COLOR, END, JUMP, STITCH, type StitchPlan, TRIM } from "../engine/plan";
import { asciiLabel, ByteWriter } from "./bytes";
import { hexToInt, nearestIndex, PEC_PALETTE } from "./palettes";

const PEC_ICON_W = 48;
const PEC_ICON_H = 38;
const JUMP_CODE = 0x10;
const TRIM_CODE = 0x20;

/** pyembroidery PecGraphics.blank (테두리가 그려진 48×38 비트맵, 228바이트) */
// prettier-ignore
const BLANK = [
  0x00,0x00,0x00,0x00,0x00,0x00,0xF0,0xFF,0xFF,0xFF,0xFF,0x0F,0x08,0x00,0x00,0x00,0x00,0x10,0x04,0x00,0x00,0x00,0x00,0x20,
  0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,
  0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,
  0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,
  0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,
  0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,
  0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,
  0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,
  0x02,0x00,0x00,0x00,0x00,0x40,0x02,0x00,0x00,0x00,0x00,0x40,0x04,0x00,0x00,0x00,0x00,0x20,0x08,0x00,0x00,0x00,0x00,0x10,
  0xF0,0xFF,0xFF,0xFF,0xFF,0x0F,0x00,0x00,0x00,0x00,0x00,0x00,
];

interface Bounds {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

function bounds(plan: StitchPlan): Bounds {
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (let i = 0; i < plan.cmds.length; i++) {
    const c = plan.cmds[i];
    if (c !== STITCH && c !== JUMP) continue;
    const x = plan.xs[i],
      y = plan.ys[i];
    if (x < x0) x0 = x;
    if (y < y0) y0 = y;
    if (x > x1) x1 = x;
    if (y > y1) y1 = y;
  }
  if (!isFinite(x0)) return { x0: 0, y0: 0, x1: 0, y1: 0 };
  return { x0, y0, x1, y1 };
}

/** 계획의 색 블록별 실 색 → PEC 팔레트 번호 (서로 다른 색은 다른 번호가 되도록) */
export function pecColorIndices(colors: string[]): number[] {
  const palette = PEC_PALETTE.slice();
  const chart: (string | null)[] = new Array(palette.length).fill(null);
  const unique = [...new Set(colors)];
  for (const c of unique) {
    const idx = nearestIndex(hexToInt(c), palette);
    if (idx < 0) break;
    palette[idx] = null;
    chart[idx] = c;
  }
  return colors.map((c) => {
    const i = chart.indexOf(c);
    return i >= 0 ? i : nearestIndex(hexToInt(c), PEC_PALETTE);
  });
}

function writePecValue(w: ByteWriter, v: number, long: boolean, flag = 0) {
  if (!long && v > -64 && v < 63) {
    w.u8(v & 0x7f);
    return;
  }
  let x = v & 0x0fff;
  x |= 0x8000;
  x |= flag << 8;
  w.u16be(x);
}

function pecEncode(w: ByteWriter, plan: StitchPlan) {
  let colorTwo = true;
  let jumping = true;
  let init = true;
  let trimPending = false;
  let px = 0,
    py = 0;
  for (let i = 0; i < plan.cmds.length; i++) {
    const c = plan.cmds[i];
    const x = plan.xs[i],
      y = plan.ys[i];
    const dx = x - px,
      dy = y - py;
    if (c === STITCH) {
      if (trimPending) {
        // 트림 뒤 점프가 없었으면 제자리 트림 점프를 넣는다
        writePecValue(w, 0, true, TRIM_CODE);
        writePecValue(w, 0, true, TRIM_CODE);
        trimPending = false;
        jumping = true;
      }
      if (jumping) {
        if (dx !== 0 && dy !== 0) {
          writePecValue(w, 0, false);
          writePecValue(w, 0, false);
        }
        jumping = false;
      }
      writePecValue(w, dx, false);
      writePecValue(w, dy, false);
      px = x;
      py = y;
    } else if (c === JUMP) {
      jumping = true;
      const flag = init ? JUMP_CODE : trimPending ? TRIM_CODE : JUMP_CODE;
      writePecValue(w, dx, true, flag);
      writePecValue(w, dy, true, flag);
      trimPending = false;
      px = x;
      py = y;
    } else if (c === TRIM) {
      trimPending = true;
    } else if (c === COLOR) {
      if (jumping) {
        writePecValue(w, 0, false);
        writePecValue(w, 0, false);
        jumping = false;
      }
      w.bytes([0xfe, 0xb0, colorTwo ? 2 : 1]);
      colorTwo = !colorTwo;
    } else if (c === END) {
      w.u8(0xff);
      break;
    }
    init = false;
  }
}

function markBit(g: number[], x: number, y: number) {
  const i = y * 6 + Math.floor(x / 8);
  if (x < 0 || y < 0 || x >= 48 || i >= g.length || i < 0) return;
  g[i] |= 1 << x % 8;
}

function drawScaled(b: Bounds, pts: [number, number][], g: number[], buffer: number) {
  let dw = b.x1 - b.x0,
    dh = b.y1 - b.y0;
  if (dw === 0) dw = 1;
  if (dh === 0) dh = 1;
  const gw = 6 * 8,
    gh = g.length / 6;
  const scale = Math.min((gw - buffer) / dw, (gh - buffer) / dh);
  const cx = (b.x1 + b.x0) / 2,
    cy = (b.y1 + b.y0) / 2;
  const tx = -cx * scale + gw / 2,
    ty = -cy * scale + gh / 2;
  for (const [x, y] of pts) markBit(g, Math.floor(x * scale + tx), Math.floor(y * scale + ty));
}

export function writePec(w: ByteWriter, plan: StitchPlan, name: string) {
  const b = bounds(plan);
  const label = asciiLabel(name, 8);
  w.ascii(`LA:${label.padEnd(16, " ")}\r`);
  w.bytes([0x20, 0x20, 0x20, 0x20, 0x20, 0x20, 0x20, 0x20, 0x20, 0x20, 0x20, 0x20, 0xff, 0x00]);
  w.u8(PEC_ICON_W / 8);
  w.u8(PEC_ICON_H);
  const idx = pecColorIndices(plan.colors);
  const count = idx.length;
  if (count) {
    w.bytes(new Array(12).fill(0x20));
    w.u8(count - 1);
    w.bytes(idx);
  } else {
    w.bytes([0x20, 0x20, 0x20, 0x20, 0x64, 0x20, 0x00, 0x20, 0x00, 0x20, 0x20, 0x20, 0xff]);
  }
  for (let i = count; i < 463; i++) w.u8(0x20);
  // 바늘땀 블록
  const start = w.pos;
  w.bytes([0x00, 0x00]);
  w.u24(0);
  w.bytes([0x31, 0xff, 0xf0]);
  w.u16(Math.round(b.x1 - b.x0));
  w.u16(Math.round(b.y1 - b.y0));
  w.u16(0x1e0);
  w.u16(0x1b0);
  pecEncode(w, plan);
  w.patchU24(start + 2, w.pos - start);
  // 미리보기 그림: 전체 1장 + 색 블록마다 1장
  const all: [number, number][] = [];
  const blocks: [number, number][][] = [[]];
  for (let i = 0; i < plan.cmds.length; i++) {
    const c = plan.cmds[i];
    if (c === STITCH) {
      all.push([plan.xs[i], plan.ys[i]]);
      blocks[blocks.length - 1].push([plan.xs[i], plan.ys[i]]);
    } else if (c === COLOR) blocks.push([]);
  }
  const g0 = BLANK.slice();
  drawScaled(b, all, g0, 4);
  w.bytes(g0);
  for (let k = 0; k < Math.max(1, count); k++) {
    const g = BLANK.slice();
    drawScaled(b, blocks[k] ?? [], g, 5);
    w.bytes(g);
  }
}

function pesString16(w: ByteWriter, s: string) {
  w.u16(s.length);
  w.ascii(s);
}

export function writePes(plan: StitchPlan, name = "JASU"): Uint8Array {
  const w = new ByteWriter(65536);
  w.ascii("#PES0001");
  const b = bounds(plan);
  const cx = (b.x1 + b.x0) / 2,
    cy = (b.y1 + b.y0) / 2;
  const left = b.x0 - cx,
    top = b.y0 - cy,
    right = b.x1 - cx,
    bottom = b.y1 - cy;
  const pecAt = w.pos;
  w.i32(0);
  const hasStitches = plan.cmds.some((c) => c === STITCH);
  // 머리글 v1: 틀에 맞춤=1, 130×180 틀=1, 블록 수
  w.u16(1);
  w.u16(1);
  w.u16(hasStitches ? 1 : 0);
  if (!hasStitches) {
    w.u16(0);
    w.u16(0);
  } else {
    w.u16(0xffff);
    w.u16(0x0000);
    writePesBlocks(w, plan, left, top, right, bottom, b);
  }
  w.patchI32(pecAt, w.pos);
  writePec(w, plan, name);
  return w.result();
}

function writePesBlocks(w: ByteWriter, plan: StitchPlan, left: number, top: number, right: number, bottom: number, b: Bounds) {
  pesString16(w, "CEmbOne");
  const width = right - left,
    height = bottom - top;
  for (let i = 0; i < 8; i++) w.u16(0);
  const hoopH = 1800,
    hoopW = 1300;
  let tx = 350,
    ty = 100 + height;
  tx += hoopW / 2;
  ty += hoopH / 2;
  tx += -width / 2;
  ty += -height / 2;
  w.f32(1);
  w.f32(0);
  w.f32(0);
  w.f32(1);
  w.f32(tx);
  w.f32(ty);
  w.u16(1);
  w.u16(0);
  w.u16(0);
  w.u16(Math.trunc(width));
  w.u16(Math.trunc(height));
  w.bytes([0, 0, 0, 0, 0, 0, 0, 0]);
  const sectionsAt = w.pos;
  w.u16(0);
  w.u16(0xffff);
  w.u16(0x0000);
  pesString16(w, "CSewSeg");
  // 구간: 같은 명령끼리 묶은 블록. 점프는 [마지막 박은 점, 점프 끝] 두 점, 바늘땀은 전부.
  const adjX = b.x0,
    adjY = b.y1;
  const colorIdx = pecColorIndices(plan.colors);
  let colorI = 0;
  let colorCode = colorIdx[0] ?? 20;
  let stitchedX = 0,
    stitchedY = 0;
  const segs: { pts: [number, number][]; color: number; flag: number }[] = [];
  let i = 0;
  const n = plan.cmds.length;
  while (i < n) {
    const c = plan.cmds[i];
    let j = i;
    while (j < n && plan.cmds[j] === c) j++;
    if (c === JUMP) {
      segs.push({
        pts: [
          [stitchedX - adjX, stitchedY - adjY],
          [plan.xs[j - 1] - adjX, plan.ys[j - 1] - adjY],
        ],
        color: colorCode,
        flag: 1,
      });
    } else if (c === STITCH) {
      const pts: [number, number][] = [];
      for (let k = i; k < j; k++) {
        stitchedX = plan.xs[k];
        stitchedY = plan.ys[k];
        pts.push([stitchedX - adjX, stitchedY - adjY]);
      }
      segs.push({ pts, color: colorCode, flag: 0 });
    } else if (c === COLOR) {
      colorI += j - i;
      colorCode = colorIdx[Math.min(colorI, colorIdx.length - 1)] ?? colorCode;
    }
    i = j;
  }
  const colorlog: [number, number][] = [];
  let prevColor = -1;
  segs.forEach((s, k) => {
    if (k > 0) w.u16(0x8003);
    if (s.color !== prevColor) {
      colorlog.push([k, s.color]);
      prevColor = s.color;
    }
    w.u16(s.flag);
    w.u16(s.color);
    w.u16(s.pts.length);
    for (const [x, y] of s.pts) {
      w.u16(Math.trunc(x) & 0xffff);
      w.u16(Math.trunc(y) & 0xffff);
    }
  });
  w.u16(colorlog.length);
  for (const [sec, col] of colorlog) {
    w.u16(sec);
    w.u16(col);
  }
  w.patchU16(sectionsAt, segs.length);
  w.u16(0x0000);
  w.u16(0x0000);
}
